import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import ReportLauncher from '../../../shared/ReportLauncher';
import PricelistCompactReport from './PricelistCompactReport';
import PricelistExtendedReport from './PricelistExtendedReport';
import { AvailabilityPill } from '../../../shared/Pill';
import ConvertedBanner from '../../../shared/ConvertedBanner';
import { useRrpSettings } from '../../product/Products/productReportShared';
import DateInput from '../../../shared/DateInput';
import { formatDate } from '../../../shared/tenantClock';
import { formatMoney, formatNumber } from '../../../shared/numberFormat';
import TaxBasisLine from '../../../shared/TaxBasisLine';

// Pricelist 2.0's generate/view screen — reached from one saved Pricelist
// row (PricelistTable.js, Page #3-18) at /pricelists/:id/generate. Scope
// (Customer Price Group/Product Price Group/UoM) comes from that saved
// record, not ad hoc form fields — a Pricelist is a named, standing
// business entity now, not a filter preset (see conversation history for
// why SavedFilterTabs was replaced with real CRUD). Valid From/To stay
// editable here even so, pre-filled from the record: previewing a
// different date is a legitimate one-off ("what will this look like after
// the scheduled change") without needing to edit the saved definition.
//
// Resolution stays live — this calls the same pricelistResolver.js (via
// GET /api/pricelist2) fresh each time, using whichever Group/Product Price Group/UoM
// the loaded record holds right now. One row per Product x sellable UoM x
// quantity tier: what this audience pays, per product, per qty, per UoM —
// not one collapsed number per product (that's Price Lookup/a future
// Quote's job — see priceResolution.js's applyPrice vs
// applyPriceTiers). The legacy Pricelists page is being retired now
// that this CRUD exists; Price Lookup stays as the future quote engine's
// own base.
function formatPrice(price) {
  return formatMoney(price) || '—';
}

// 2dp, not the Margin Report's 3dp per-Base-UoM-unit convention
// (MarginReportView.js) — that finer precision is for internal Admin
// discrepancy-checking; this document goes to external customers, who
// expect ordinary currency formatting like every other price on it.
function formatEquivPrice(price) {
  if (price === null || price === undefined || price === '') return '—';
  return formatPrice(price);
}

// Runs of consecutive rows sharing a product_id become one group — rows
// already arrive grouped by product (the resolver loops product-by-product),
// so this just finds the boundaries rather than re-sorting.
// Base Price (vis_prices-and-pricelists.md's Price section) — Retail's own
// Price for the Product's Base UoM specifically, the one figure a Pricelist
// Header shows per product regardless of how many other UoM rows/tiers
// follow it. Resolved live from this same already-fetched result (never
// stored on the Pricelist record itself, matching this feature's "always
// live, never a snapshot" design) — the row whose own UoM *is* the Base UoM
// (its uom_code matches the product's own base_uom_code) at the qty-1 tier.
function findBasePrice(rows) {
  const baseRow = rows.find((row) => row.min_quantity === 1 && row.uom_code === row.base_uom_code);
  return baseRow ? baseRow.price : null;
}

function groupByProduct(rows) {
  const groups = [];
  rows.forEach((row) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.product_id === row.product_id) {
      lastGroup.rows.push(row);
    } else {
      groups.push({
        product_id: row.product_id,
        sku: row.sku,
        name: row.name,
        availability_state: row.availability_state,
        availability_badge: row.availability_badge,
        availability_badge_color: row.availability_badge_color,
        availability_date: row.availability_date,
        rows: [row],
      });
    }
  });
  groups.forEach((group) => { group.basePrice = findBasePrice(group.rows); });
  return groups;
}

// One section per Category, each holding its own product groups — the
// resolver orders rows by Category then SKU, so (like groupByProduct) this
// only needs to find run boundaries, not re-sort.
function groupByCategory(rows) {
  const sections = [];
  rows.forEach((row) => {
    const lastSection = sections[sections.length - 1];
    if (lastSection && lastSection.category_id === row.product_category_id) {
      lastSection.rows.push(row);
    } else {
      sections.push({ category_id: row.product_category_id, category_name: row.product_category_name, rows: [row] });
    }
  });
  return sections;
}

function Pricelist2Page() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();

  const [pricelist, setPricelist] = useState(null);
  const [pricelistLoading, setPricelistLoading] = useState(true);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [reportMode, setReportMode] = useState(null); // 'compact' | 'extended' | null

  // Same tenant default-rate concept as Retail Prices'/Price
  // Pricing's/the Price Break Report's own Inc-GST figures — null when no
  // default Tax Code is configured, in which case Inc GST just shows blank
  // rather than guessing a rate. Fetched once here and passed down to both
  // print reports rather than each fetching it independently, since this
  // page already owns their shared data flow (pricelist/result props).
  // Same hook also supplies the locked track_rrp setting, which gates
  // whether either print layout may show RRP at all.
  const { trackRrp, defaultTaxRate } = useRrpSettings();
  const showRrp = trackRrp && !!pricelist?.show_rrp;

  // Takes the scope explicitly rather than reading `pricelist` state —
  // called right after setPricelist() below, before that state update has
  // actually applied, so closing over state here would read a stale null.
  const generate = async (scope, fromDate, toDate) => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.get('/api/pricelist2', {
        params: {
          customer_price_group_id: scope.customer_price_group_id,
          product_price_group_id: scope.product_price_group_id || undefined,
          uom_id: scope.uom_id || undefined,
          valid_from: fromDate || undefined,
          valid_to: toDate || undefined,
          // From the saved record, not showRrp — track_rrp may not have
          // loaded yet on first generate; the reports gate display on it.
          include_rrp: scope.show_rrp ? 1 : undefined,
        },
      });
      setResult(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Pricelist generation failed');
    }
    setLoading(false);
  };

  useEffect(() => {
    setPricelistLoading(true);
    axios.get(`/api/pricelists/${id}`)
      .then((res) => {
        const p = res.data.data;
        setPricelist(p);
        setValidFrom(p.valid_from);
        setValidTo(p.valid_to || '');
        setPricelistLoading(false);
        generate(p, p.valid_from, p.valid_to || '');
      })
      .catch((err) => {
        showError(err, 'Could not load this Pricelist');
        setPricelistLoading(false);
      });
    // eslint-disable-next-line
  }, [id]);

  if (pricelistLoading) {
    return <div style={{ width: '100%', maxWidth: 1100, margin: '32px auto', padding: 24, fontSize: 14, color: '#888' }}>Loading...</div>;
  }
  if (!pricelist) {
    return <div style={{ width: '100%', maxWidth: 1100, margin: '32px auto', padding: 24, fontSize: 14, color: '#888' }}>Pricelist not found.</div>;
  }

  return (
    <div style={{ width: '100%', maxWidth: 1100, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <button
        type="button"
        onClick={() => navigate('/sales-pricelist-2')}
        style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 10 }}
      >
        ← Back to Pricelists
      </button>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>{pricelist.name} Pricelist</h2>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
        {pricelist.customer_price_group?.name} · {pricelist.product_price_group?.name || 'All products'}{pricelist.uom ? ` · ${pricelist.uom.code}` : ''}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Valid From</label>
          <DateInput value={validFrom} onChange={(e) => setValidFrom(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Valid To</label>
          <DateInput value={validTo} onChange={(e) => setValidTo(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }} />
        </div>
        <button
          onClick={() => generate(pricelist, validFrom, validTo)}
          disabled={loading}
          style={{ padding: '8px 20px', background: loading ? '#9fb3c8' : '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? 'Generating...' : 'Regenerate'}
        </button>
        <ReportLauncher
          options={[
            { key: 'compact', label: 'Compact Pricelist', onSelect: () => setReportMode('compact') },
            { key: 'extended', label: 'Extended Pricelist', onSelect: () => setReportMode('extended') },
          ]}
          activeContent={
            reportMode === 'compact' ? (
              <PricelistCompactReport pricelist={pricelist} result={result} defaultTaxRate={defaultTaxRate} showRrp={showRrp} onClose={() => setReportMode(null)} />
            ) : reportMode === 'extended' ? (
              <PricelistExtendedReport pricelist={pricelist} result={result} defaultTaxRate={defaultTaxRate} showRrp={showRrp} onClose={() => setReportMode(null)} />
            ) : null
          }
          onCloseActive={() => setReportMode(null)}
        />
      </div>

      {result && (
        <div style={{ marginTop: 24 }}>
          {result.omitted && result.omitted.length > 0 && (
            <ConvertedBanner
              title={`${result.omitted.length} Product/UoM${result.omitted.length === 1 ? '' : 's'} left off this Pricelist`}
              message={`No Retail Price as of ${formatDate(result.validFrom)}: ${result.omitted.slice(0, 5).map((o) => `${o.sku}${o.uom_code ? ` / ${o.uom_code}` : ''}`).join(', ')}${result.omitted.length > 5 ? `, and ${result.omitted.length - 5} more` : ''}. Run the Retail Price Coverage check to see why.`}
              linkTo="/inventory-reports"
              linkLabel="Open Product Integrity"
            />
          )}
          <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
            {result.rows.length} row{result.rows.length === 1 ? '' : 's'} — valid {formatDate(result.validFrom)}{result.validTo !== result.validFrom ? ` to ${formatDate(result.validTo)}` : ''}
          </div>
          {result.rows.length === 0 ? (
            <div style={{ fontSize: 14, color: '#888', padding: '10px 0' }}>No sellable Product/UoM combinations match this scope.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {groupByCategory(result.rows).map((section) => (
                <div key={section.category_id ?? 'uncategorised'}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 10px', paddingBottom: 6, borderBottom: '2px solid #dde4ec' }}>
                    {section.category_name}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {groupByProduct(section.rows).map((group) => (
                      <div key={group.product_id} style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', breakInside: 'avoid' }}>
                        <div style={{ padding: '10px 14px', fontWeight: 600, background: '#f6f8fa', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span>{group.sku} — {group.name}</span>
                          <AvailabilityPill badge={group.availability_badge} date={group.availability_date} color={group.availability_badge_color} />
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                          <thead>
                            <tr style={{ background: '#eef2f7' }}>
                              <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: 12, color: '#666' }}>UoM</th>
                              <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12, color: '#666' }}>Min Qty</th>
                              <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12, color: '#666' }}>UoM Price<TaxBasisLine>ex GST</TaxBasisLine></th>
                              <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12, color: '#666' }}>UoM Price<TaxBasisLine>inc GST</TaxBasisLine></th>
                              <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12, color: '#666' }}>Equiv Price</th>
                              <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: 12, color: '#666' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.rows.map((row, idx) => {
                              const isNewUom = idx === 0 || group.rows[idx - 1].uom_id !== row.uom_id;
                              return (
                                <tr key={`${row.uom_id}-${row.min_quantity}-${idx}`} style={{ borderTop: isNewUom ? '1px solid #e0e0e0' : 'none' }}>
                                  <td style={{ padding: '8px 10px', textAlign: 'center', color: '#888' }}>{isNewUom ? row.uom_name : ''}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatNumber(row.min_quantity, { maxDecimals: 4 })}+</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                                    {row.ambiguous ? (
                                      <span style={{ color: '#b25f00', fontWeight: 600 }} title="More than one equally-specific Price matches — fix the overlapping Scope before this can resolve">⚠ Ambiguous</span>
                                    ) : row.needs_price ? (
                                      <span style={{ color: '#888' }} title="A Fixed Override applies here but no price has been entered yet">Needs price</span>
                                    ) : formatPrice(row.price)}
                                  </td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555' }}>
                                    {row.ambiguous || row.needs_price ? '—' : (defaultTaxRate !== null ? formatPrice(row.price * (1 + defaultTaxRate)) : '—')}
                                  </td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555' }}>
                                    {row.ambiguous || row.needs_price ? '—' : `${formatEquivPrice(row.equiv_price)}/${row.base_uom_name}`}
                                  </td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                    {row.change_warning_date && (
                                      <span style={{ color: '#b25f00' }} title={`Price is only guaranteed through ${formatDate(row.change_warning_date)} — before this list's Valid To (${formatDate(result.validTo)})`}>⚠</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Pricelist2Page;
